import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core'

import * as d3 from 'd3'
import { debounceTime, filter, Subject, takeUntil, tap } from 'rxjs'

import { DestroyDirective } from '~/core/directives'
import { ISizeElement } from '~/core/models'
import {
  drawIsolineArea,
  drawIsolineLegend,
  drawIsolineLine,
  drawXLabel,
  drawYLabel,
  generateIsolineTooltipValues,
} from '~/utils/d3-isoline.utils'
import { addInfo, drawDot, drawLine, forecastNumber } from '~/utils/index'

import { IChartIsolineData, IChartIsolineSettings } from '../../models'

const MAX_WIDTH = 700
const TICK_SIZE = 60

@Component({
  selector: 'app-eps-isoline-chart',
  template: '<div class="eps-chart" #chart></div>',
  styleUrl: './eps-isoline-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  standalone: true,
})
export class EpsIsolineChartComponent implements OnInit, OnChanges {
  @Input() chartOptions: IChartIsolineSettings | null = null
  @Input() ticker = ''
  @Output() sizeChange = new EventEmitter<ISizeElement>()

  @ViewChild('chart', { static: true }) chartElement?: ElementRef

  @HostListener('window:resize', ['$event']) onResize(): void {
    this.drawChart$.next(null)
  }

  private readonly destroy$ = inject(DestroyDirective).destroy$
  private drawChart$ = new Subject()
  private MARGIN = 50
  private PADDING = 40
  private OFFSET = this.MARGIN - this.PADDING
  private width = 0
  private height = 0

  constructor(private renderer: Renderer2) {}

  ngOnChanges(): void {
    this.drawChart$.next(null)
  }

  ngOnInit(): void {
    this.drawChart$
      .pipe(
        debounceTime(100),
        tap(() => this.removeChart()),
        tap(() => this.setSize()),
        filter(() => Boolean(this.chartOptions?.data)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.drawChart())
    this.drawChart$.next(null)
  }

  private removeChart(): void {
    const nativeElement = this.chartElement?.nativeElement
    nativeElement.childNodes.forEach((node: any) => this.renderer.removeChild(nativeElement, node))
  }

  private setSize(): void {
    this.width = Math.min(this.chartElement?.nativeElement.offsetWidth, MAX_WIDTH) - this.MARGIN
    this.height = this.width
    this.sizeChange.emit({ width: this.width, height: this.height })
  }

  private drawChart(): void {
    const translates = this.chartOptions!.translates
    const marginLeftContainer = this.MARGIN

    for (let child of this.chartElement?.nativeElement.children) {
      this.renderer.removeChild(this.chartElement?.nativeElement, child)
    }

    const xMax = this.chartOptions!.xMax
    const xMin = this.chartOptions!.xMin
    const yMax = this.chartOptions!.yMax
    const yMin = this.chartOptions!.yMin

    const yInfoMax = yMax

    const y = d3.scaleLinear().domain([yMin, yMax]).range([this.height, 0])
    const x = d3.scaleLinear().domain([xMin, xMax]).range([0, this.width])

    const sortedPercentKeys = Object.keys(this.chartOptions!.data!).sort((a, b) => +b - +a)
    const sortedPercents: number[] = []

    const data: IChartIsolineData = {}
    const hoverData: IChartIsolineData = {}

    for (const sortedPercentKey of sortedPercentKeys) {
      const percent = +sortedPercentKey
      const percentData = this.chartOptions!.data![percent]
      let resultData = percentData
      let resultHoverData = percentData

      if (percentData.length <= 1) {
        continue
      }

      if (percentData.length > 1) {
        const xy1 = percentData[1]
        const xy0 = percentData[0]
        let y2 = xy0.y > xy1.y ? yMax : yMin
        let x2 = xy0.x + (xy1.x - xy0.x) * ((y2 - xy0.y) / (xy1.y - xy0.y))

        if (x2 > xMax) {
          x2 = xMax
          y2 = xy0.y + ((xy1.y - xy0.y) / (xy1.x - xy0.x)) * (x2 - xy0.x)
        }

        const xy = { x: x2, y: y2 }
        resultData = [xy, ...percentData]
        resultHoverData = [xy, ...percentData]

        if (y2 === yMax) {
          resultHoverData.unshift({ x: xMax, y: yMax })
        }
      }

      data[percent] = resultData
      hoverData[percent] = resultHoverData
      sortedPercents.push(percent)
    }

    const ticksX = Math.round(this.width / TICK_SIZE)
    const ticksY = Math.round(ticksX * 1.5)

    const yAxis = d3
      .axisLeft(y)
      .ticks(ticksY)
      .tickFormat((domainValue: any, _index: number) =>
        forecastNumber(domainValue, this.chartOptions!.numberSettings.y.toFixed)
      )

    const xAxis = d3
      .axisBottom(x)
      .ticks(ticksX)
      .tickFormat((domainValue: any, _index: number) =>
        forecastNumber(domainValue, this.chartOptions!.numberSettings.x.toFixed)
      )

    const makeXGridLines = (): d3.Axis<d3.NumberValue> => d3.axisBottom(x).ticks(ticksX)
    const makeYGridLines = (): d3.Axis<d3.NumberValue> => d3.axisLeft(y).ticks(ticksY)

    const area = d3
      .area()
      .curve(d3.curveBasis)
      .x(function (d: any) {
        return x(d[0])
      })
      .y0(this.height)
      .y1(function (d: any) {
        return y(d[1])
      })

    const line = d3
      .line()
      .curve(d3.curveBasis)
      .x(function (d: any) {
        return x(d[0])
      })
      .y(function (d: any) {
        return y(d[1])
      })

    const svg = d3
      .select(this.chartElement?.nativeElement)
      .append('svg')
      .attr('height', this.height + this.MARGIN)
      .attr('width', this.width + this.MARGIN)
      .attr('padding-top', this.OFFSET)

    const chartGroup = svg.append('g').attr('transform', 'translate(' + marginLeftContainer + ',' + 2 + ')')

    chartGroup
      .append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.2)
      .attr('transform', `translate(${0}, ${this.height})`)
      .call(
        makeXGridLines()
          .tickSize(-this.height)
          .tickFormat(() => '')
      )

    chartGroup
      .append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.2)
      .call(
        makeYGridLines()
          .tickSize(-this.width)
          .tickFormat(() => '')
      )

    drawXLabel(chartGroup, this.width, this.height, translates.xLabel)
    drawYLabel(chartGroup, this.width, this.height, translates.yLabel)

    const lineSettings = d3
      .line()
      .x(function (d: any) {
        return x(d[0])
      })
      .y(function (d: any) {
        return y(d[1])
      })

    this.drawConsensusValue(chartGroup, lineSettings, xMin, yMin, x, y)
    this.drawAIValue(chartGroup, lineSettings, xMin, yMin, x, y)

    chartGroup
      .append('g')
      .attr('class', 'axis x')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(xAxis)

    chartGroup.append('g').attr('class', 'axis x').attr('transform', `translate(${0}, ${0})`).call(yAxis)

    for (const percent of sortedPercents) {
      const resultData = data[percent]
      drawIsolineLine({
        line,
        chartGroup,
        data: resultData,
        strokeType: percent === 0 ? 'dash' : 'line',
        color: percent === 0 ? 'var(--proximilar-chart-line-info-color)' : 'var(--proximilar-chart-line-color)',
      })
    }

    let prevXY = { x: xMin, y: yMin }

    for (const percent of sortedPercents) {
      const resultData = data[percent]
      prevXY = this.drawInfo(chartGroup, x, y, prevXY, xMin, percent, resultData)
    }

    drawIsolineLegend({
      chartGroup,
      x,
      y,
      xPos: xMax,
      yPos: yInfoMax,
      pdfConsensus: translates.pdfConsensus || '',
      pdfForecastProximilar: translates.pdfForecastProximilar || '',
      lineExpected: translates.lineExpected || '',
    })

    this.drawTooltip({
      area,
      chartGroup,
      x,
      y,
      yMin,
      yMax,
      xMin,
      xMax,
      sortedKeys: sortedPercents,
      hoverData,
    })
  }

  private drawInfo(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>,
    prevXY: { x: number; y: number },
    x1: number,
    percent: number,
    data: { x: number; y: number }[]
  ): { x: number; y: number } {
    const xy = data.reduce((d, current) => (d.x < current.x ? d : current), data[0])

    addInfo({
      chartGroup,
      title: (percent * 100).toFixed(1) + '%',
      xVal: x(x1) + 10,
      yVal: y(xy.y),
      positionX: 'right',
      color: 'var(--proximilar-chart-line-info-color)',
    })

    return prevXY
  }

  private drawTooltip({
    area,
    chartGroup,
    x,
    y,
    yMin,
    yMax,
    xMin,
    xMax,
    sortedKeys,
    hoverData,
  }: {
    area: d3.Line<[number, number]>
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>
    x: d3.ScaleLinear<number, number, never>
    y: d3.ScaleLinear<number, number, never>
    yMin: number
    yMax: number
    xMin: number
    xMax: number
    sortedKeys: number[]
    hoverData: IChartIsolineData
  }): void {
    const tooltipTitleText = this.getTooltipTitle(0, sortedKeys, true)

    const tooltipSettings = generateIsolineTooltipValues({
      el: this.chartElement?.nativeElement,
      chartGroup,
      x,
      y,
      xMin,
      xMax,
      yMin,
      yMax,
      height: this.height,
      width: this.width,
      tooltipTitleText,
      yToFixed: this.chartOptions!.numberSettings.y.toFixed,
      xToFixed: this.chartOptions!.numberSettings.x.toFixed,
    })

    for (let i = 0; i < sortedKeys.length; i++) {
      const key = +sortedKeys[i]
      const resultHoverData = hoverData[key]
      const tooltipTitleText = this.getTooltipTitle(i, sortedKeys)
      drawIsolineArea({
        area,
        chartGroup,
        data: resultHoverData,
        x,
        y,
        yMin,
        xMin,
        yToFixed: this.chartOptions!.numberSettings.y.toFixed,
        xToFixed: this.chartOptions!.numberSettings.x.toFixed,
        tooltipTitleText,
        tooltipSettings,
        opacity: 0,
      })
    }

    const { lineXValue, lineYValue } = tooltipSettings
    lineXValue.raise()
    lineYValue.raise()
  }

  private getTooltipTitle(i: number, percentArr: number[], isOutOfChart = false): string {
    const percent = Math.round(percentArr[i] * 10000) / 100

    if (isOutOfChart) {
      return `${this.ticker} stock is expected to go up by ${Math.abs(
        percent
      )}% or more if its results are in this region`
    }

    if (i === percentArr.length - 1) {
      return `${this.ticker} stock is expected to drop by ${Math.abs(
        percent
      )}% or more if its results are in this region`
    }

    const nextPercent = Math.round(percentArr[i + 1] * 10000) / 100

    if (percent < 0 || nextPercent < 0) {
      return `${this.ticker} stock is expected to drop by ${Math.abs(percent)}% to ${Math.abs(
        nextPercent
      )}% if its results are in this region`
    }

    return `${this.ticker} stock is expected to go up by ${Math.abs(nextPercent)}% to ${Math.abs(
      percent
    )}% if its results are in this region`
  }

  private drawConsensusValue(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    lineSettings: d3.Line<[number, number]>,
    xMin: number,
    yMin: number,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>
  ): void {
    drawLine(
      chartGroup,
      lineSettings([
        [this.chartOptions!.revPdfConsensus, yMin],
        [this.chartOptions!.revPdfConsensus, this.chartOptions!.epsPdfConsensus],
      ]),
      'var(--proximilar-chart-info1-color)',
      2,
      0.5
    )

    drawLine(
      chartGroup,
      lineSettings([
        [xMin, this.chartOptions!.epsPdfConsensus],
        [this.chartOptions!.revPdfConsensus, this.chartOptions!.epsPdfConsensus],
      ]),
      'var(--proximilar-chart-info1-color)',
      2,
      0.5
    )

    drawDot(
      chartGroup,
      x(this.chartOptions!.revPdfConsensus),
      y(this.chartOptions!.epsPdfConsensus),
      'var(--proximilar-chart-info1-color)'
    )
  }

  private drawAIValue(
    chartGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    lineSettings: d3.Line<[number, number]>,
    xMin: number,
    yMin: number,
    x: d3.ScaleLinear<number, number, never>,
    y: d3.ScaleLinear<number, number, never>
  ): void {
    drawLine(
      chartGroup,
      lineSettings([
        [this.chartOptions!.revPdfForecastProximilar, yMin],
        [this.chartOptions!.revPdfForecastProximilar, this.chartOptions!.epsPdfForecastProximilar],
      ]),
      'var(--proximilar-chart-info2-color)',
      2,
      0.5
    )

    drawLine(
      chartGroup,
      lineSettings([
        [xMin, this.chartOptions!.epsPdfForecastProximilar],
        [this.chartOptions!.revPdfForecastProximilar, this.chartOptions!.epsPdfForecastProximilar],
      ]),
      'var(--proximilar-chart-info2-color)',
      2,
      0.5
    )

    drawDot(
      chartGroup,
      x(this.chartOptions!.revPdfForecastProximilar),
      y(this.chartOptions!.epsPdfForecastProximilar),
      'var(--proximilar-chart-info2-color)'
    )
  }
}
